import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { ManualChargePostingApprovalWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ManualChargePostingApprovalTable } from "../../../containers/tables/manualchargepostingtable";
import Cookies from "universal-cookie";
import { BaseConfirmationModal } from "../../../components/modal/confirmation";
import { retrieveAllPendingManualChargeService } from "../../../utils/api/tellermanagement/manualchargeposting/retrieveAllPendingManualCharge";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { camelCaseToPascalCaseFormatter } from "../../../helpers/camelCaseToPascalCaseFormatter";
import { actOnPendingManualChargePostingService } from "../../../utils/api/approval/manualchargeapproval/actOnPendingManualCharge";
import { retrieveChartOfAccountService } from "../../../utils/api/accounting/chartofaccount/retrieveChartOfAccount";
import { retrieveSystemChargeByGLService } from "../../../utils/api/configuration/systemcharge/retrieveSystemChargeByGL";
import { retrieveSavingsProductService } from "../../../utils/api/configuration/savingsproduct/retrieveSavingsProduct";
import { retrieveLoanProductService } from "../../../utils/api/configuration/loanproduct/retrieveLoanProduct";
import { BaseButton } from "../../../components/button/styled";

export const ManualChargePostingApproval = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [action, setAction] = useState<string | null>(null);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [formDetails, setFormDetails] = useState({
		comment: "",
	});
	const [selectedCharge, setSelectedCharge] = useState<Record<string, any>>({
		id: "",
		name: "",
		type: "",
		amount: "",
		institution: "",
	});
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Approval");
	}, [setActiveNavItem]);

	const { data: manualChargePostingsToBeApproved, refetch } = useQuery({
		queryKey: ["manualChargePostings", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllPendingManualChargeService(TOKEN);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			const charges = response.data;
			setIsFetching(false);
			return charges;
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		if (!isConfirmationModalOpen && TOKEN) {
			refetch();
		}
	}, [isConfirmationModalOpen, TOKEN, refetch]);

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleSelectCharge = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		charge: Record<string, any>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedCharge(charge);
	};

	const handleActionOnPendingManualChargePosting = async () => {
		try {
			const payload = {
				action,
				...(action === "Decline" ? { comment: formDetails.comment } : {}),
			};
			const response = await actOnPendingManualChargePostingService(
				TOKEN,
				selectedCharge.institution,
				selectedCharge.id,
				payload,
			);
			if (response.status !== "success") {
				setError(`${action} posting failed. Please try again.`);
			} else {
				setError(null);
			}
			setIsLoading(false);
			setAction(null);
			setFormDetails({
				comment: "",
			});
			setSelectedCharge({
				id: "",
				name: "",
				type: "",
				amount: "",
				institution: "",
			});
			return setIsConfirmationModalOpen(false);
		} catch (error: any) {
			console.error(`${action} posting failed:`, error);
			setIsLoading(false);
			return setError(`${action} posting failed. ${error.message}`);
		}
	};

	const handleConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setIsLoading(true);
		if (!action || !selectedCharge.id) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				await handleActionOnPendingManualChargePosting();
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setAction(null);
				setFormDetails({
					comment: "",
				});
				setSelectedCharge({
					id: "",
					name: "",
					type: "",
					amount: "",
					institution: "",
				});
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handleOpenConfirmationModal = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleActionItemClick = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		selectedChargeId: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!selectedChargeId) return;
		setAction(action);
		return handleOpenConfirmationModal();
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setSelectedCharge({
				id: "",
				name: "",
				type: "",
				amount: "",
				institution: "",
			});
		}
    };
    
    const handlePagination = (
			e: React.MouseEvent<HTMLButtonElement>,
			type: "previous" | "next",
		) => {
			e.preventDefault();
			setPaginationIndex((prev) => {
				const currentPage = parseInt(prev.page, 10);
				const newPage =
					type === "previous"
						? Math.max(1, currentPage - 1)
						: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
				return {
					...prev,
					page: newPage.toString(),
				};
			});
		};

	useEffect(() => {
		// ensure that the empty string value of initial state is matched
		if (selectedCharge.id !== "" && !isConfirmationModalOpen) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedCharge, isConfirmationModalOpen]);

	const confirmationModalBody = (
		<Box>
			<Stack
				paddingTop={"calc(var(--basic-padding)/1.5)"}
				gap={{ mobile: "calc(var(--flex-gap)/2)" }}
			>
				{["name", "type", "amount"].map((key) => (
					<Stack
						key={key}
						direction={{ mobile: "row" }}
						gap={{ mobile: "calc(var(--flex-gap)/4)" }}
						justifyContent="space-between"
					>
						<Typography
							variant="h3"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={16}
							lineHeight={"normal"}
						>
							{camelCaseToPascalCaseFormatter(key)}
						</Typography>
						<Typography
							variant="body1"
							fontFamily={"Inter"}
							fontWeight={500}
							fontSize={16}
							lineHeight={"normal"}
						>
							{key === "amount"
								? formatAmountDisplay(selectedCharge[key])
								: selectedCharge[key]}
						</Typography>
					</Stack>
				))}
				{action === "Decline" && (
					<form>
						<BaseFieldSet sx={{ display: "flex", flexDirection: "column" }}>
							<BaseInput
								required
								multiline
								minRows={4}
								name="comment"
								value={formDetails.comment}
								placeholder="Enter reason for decline."
								onChange={(e) => handleChange(e)}
							/>
						</BaseFieldSet>
					</form>
				)}
			</Stack>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
					marginBlockStart={"calc(var(--basic-margin)/1)"}
				>
					{error}
				</Typography>
			)}
		</Box>
	);

	return (
		<AppLayout
			pageId={"Manual-Charge-Posting-Approval"}
			pageTitle={"Manual Charge Posting Approval"}
		>
			<ManualChargePostingApprovalWrapper>
				{/* alert modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title={`Confirm ${action === "Decline" ? "Disapproval" : "Approval"}`}
					message={`Are you sure you want to ${action?.toLowerCase()} this charge?`}
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={handleConfirmationModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box component={"div"} className="manual-charge-posting-table-box">
					<ManualChargePostingApprovalTable
						rows={manualChargePostingsToBeApproved as Record<string, any>[]}
						dropdownRef={dropdownRef}
						selectedChargeId={selectedCharge.id}
						handleActionButtonClick={handleSelectCharge}
						handleActionItemClick={handleActionItemClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</ManualChargePostingApprovalWrapper>
		</AppLayout>
	);
};
